import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { ChemicalPdfExportRequest } from '../request-models/chemical-pdf-export-request.model';
import { ChemicalPdfExportResponse } from '../response-models/chemical-pdf-export-response.model';
import { ChemicalReportAggregate } from '../response-models/chemical-report-aggregate.model';
import { ChemicalReportFilterOptions } from '../response-models/chemical-report-filter-options.model';
import { ChemicalReportOrgDetail } from '../response-models/chemical-report-org-detail.model';
import { ChemicalReportSummary } from '../response-models/chemical-report-summary.model';
import {
  reportOrNull,
  toChemicalFilterOptions,
  toChemicalReportAggregate,
  toChemicalReportOrgDetail,
  toChemicalReportSummary,
  toPdfExportResponse,
} from '../utils/report-wire-utils';

/**
 * Chemical usage reports: `/admin/chemical-reports` (hub `admin-chemical-reports-controller.ts`). Dates are
 * `YYYY-MM-DD`. Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class ChemicalReportService extends ApiService {
  constructor() {
    super('admin/chemical-reports');
  }

  /** GET /admin/chemical-reports/organization/:organizationId, optionally for one chemical and/or drone. */
  getOrganizationDetail(
    organizationId: string,
    startDate: string,
    endDate: string,
    chemicalId?: string,
    droneId?: string
  ): Promise<ChemicalReportOrgDetail | null> {
    const query = { startDate, endDate, chemicalId: chemicalId || undefined, droneId: droneId || undefined };
    const opts = { path: ['organization', organizationId], query };
    return firstValueFrom(this.request('GET', opts, data => reportOrNull(data, toChemicalReportOrgDetail)));
  }

  /** GET /admin/chemical-reports/summary, optionally for one manufacturer (partner reporting). */
  getSummary(startDate: string, endDate: string, manufacturer?: string): Promise<ChemicalReportSummary | null> {
    const opts = { path: 'summary', query: { startDate, endDate, manufacturer: manufacturer || undefined } };
    return firstValueFrom(this.request('GET', opts, data => reportOrNull(data, toChemicalReportSummary)));
  }

  /** GET /admin/chemical-reports/chemical-aggregate: one row per chemical across all organizations. */
  getChemicalAggregate(startDate: string, endDate: string, manufacturer?: string): Promise<ChemicalReportAggregate | null> {
    const opts = { path: 'chemical-aggregate', query: { startDate, endDate, manufacturer: manufacturer || undefined } };
    return firstValueFrom(this.request('GET', opts, data => reportOrNull(data, toChemicalReportAggregate)));
  }

  /** GET /admin/chemical-reports/filters: organizations, manufacturers, chemicals and date range hints. */
  getFilters(): Promise<ChemicalReportFilterOptions> {
    return firstValueFrom(this.request('GET', { path: 'filters' }, data => reportOrNull(data, toChemicalFilterOptions) ?? emptyFilters()));
  }

  /**
   * POST /admin/chemical-reports/export-pdf. The hub renders the PDF, stores it in S3 and returns a pre-signed
   * link, so this is a JSON call rather than a `download()`.
   */
  exportPdf(
    reportType: ChemicalPdfExportRequest['reportType'],
    filters: ChemicalPdfExportRequest['filters'],
    data: ChemicalPdfExportRequest['data']
  ): Promise<ChemicalPdfExportResponse | null> {
    const body: ChemicalPdfExportRequest = { reportType, filters, data };
    return firstValueFrom(this.request('POST', { path: 'export-pdf' }, result => reportOrNull(result, toPdfExportResponse), body));
  }
}

function emptyFilters(): ChemicalReportFilterOptions {
  return { organizations: [], manufacturers: [], chemicals: [], dateRangeHints: { minDate: null, maxDate: null } };
}
